import React, { useMemo, useState } from "react";
import { superService } from "../../services";
import { PLANS, PLAN_CYCLES, getPlan, resolveSubscription, clampSeats } from "../../config/plans";
import { useApp } from "../../context/AppContext";
import { useI18n } from "../../i18n";
import { Badge, Button, DataTable, Input, Modal, PageHead, Select, StatCard, Textarea, baht, formatDate, useAsync } from "../../components/ui";
import Icon from "../../components/ui/Icon";

const STATUS_TONE = { active: "green", expired: "rose", none: "gray" };

// ร้านทั้งหมดของเว็บนี้ + เปิด/แก้แพ็กเกจให้ร้าน (ไม่ผ่าน QR)
export default function SuperOrganizations() {
  const { profile, toast } = useApp();
  const { tx, pick } = useI18n();
  const { data: orgs, loading, reload } = useAsync(() => superService.organizations(), [], []);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);

  const rows = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    return (orgs || [])
      .map((org) => ({ ...org, sub: resolveSubscription(org) }))
      .filter((org) => !keyword || [org.name, org.code, org.ownerEmail, org.email].some((value) => String(value || "").toLowerCase().includes(keyword)));
  }, [orgs, search]);

  const count = (status) => rows.filter((row) => row.sub.status === status).length;

  return (
    <>
      <PageHead title={tx("ร้านทั้งหมด", "All shops")} subtitle={tx("ร้านทุกร้านของเว็บนี้ พร้อมแพ็กเกจและวันหมดอายุ", "Every shop on this site with its plan and expiry")} />

      <div className="grid-3 u-mb-16">
        <StatCard label={tx("ร้านทั้งหมด", "Shops")} value={rows.length} icon={<Icon name="building" />} />
        <StatCard label={tx("แพ็กเกจใช้งานอยู่", "Active plans")} value={count("active")} icon={<Icon name="success" />} />
        <StatCard label={tx("หมดอายุ / ยังไม่ซื้อ", "Expired / none")} value={count("expired") + count("none")} icon={<Icon name="warning" />} />
      </div>

      <div className="u-mb-16" style={{ maxWidth: 360 }}>
        <Input placeholder={tx("ค้นหาชื่อร้าน / รหัสร้าน / อีเมล", "Search name / code / email")} value={search} onChange={(event) => setSearch(event.target.value)} />
      </div>

      <DataTable
        loading={loading}
        rows={rows}
        columns={[
          { key: "name", label: tx("ร้าน", "Shop"), render: (row) => <div><div className="u-strong">{row.name || row.id}</div><div className="u-fs-12 u-muted">{row.code || row.id}</div></div> },
          { key: "plan", label: tx("แพ็กเกจ", "Plan"), render: (row) => (row.sub.plan ? `${pick(row.sub.plan)}${row.sub.seats ? ` · ${row.sub.seats} User` : ""}` : "-") },
          { key: "status", label: tx("สถานะ", "Status"), render: (row) => <Badge tone={STATUS_TONE[row.sub.status]}>{row.sub.status === "active" ? tx("ใช้งานอยู่", "Active") : row.sub.status === "expired" ? tx("หมดอายุ", "Expired") : tx("ยังไม่ซื้อ", "No plan")}</Badge> },
          { key: "expire", label: tx("ใช้ได้ถึง", "Valid until"), render: (row) => (row.sub.expireAt ? `${formatDate(row.sub.expireAt)}${row.sub.isActive ? ` (${row.sub.daysLeft} ${tx("วัน", "d")})` : ""}` : "-") },
          { key: "addons", label: tx("ฟีเจอร์เสริม", "Add-ons"), align: "right", render: (row) => row.sub.addonModules.length },
          { key: "action", label: "", align: "right", render: (row) => <Button size="sm" onClick={() => setEditing(row)}>{tx("แพ็กเกจ", "Plan")}</Button> },
        ]}
        emptyText={tx("ยังไม่มีร้าน", "No shops yet")}
      />

      {editing ? (
        <PlanEditor
          org={editing}
          by={profile?.email || "super_user"}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            reload();
            toast(tx("บันทึกแพ็กเกจแล้ว", "Plan saved"), "success");
          }}
        />
      ) : null}
    </>
  );
}

const dateInput = (value) => (value ? new Date(value).toISOString().slice(0, 10) : "");

function PlanEditor({ org, by, onClose, onSaved }) {
  const { tx, pick } = useI18n();
  const { toast } = useApp();
  const sub = org.sub;
  const initialPlan = getPlan(sub.planKey) || PLANS[0];
  const [form, setForm] = useState({
    planKey: initialPlan.key,
    // ร้านเดิมที่ไม่จำกัด User (seats = null) ต้องให้ทีมงานเห็นตัวเลขที่จะบันทึกจริง
    seats: initialPlan.perUser ? clampSeats(initialPlan.key, sub.seats || initialPlan.maxSeats) : "",
    cycle: sub.cycle || "monthly",
    expireAt: dateInput(sub.expireAt) || dateInput(new Date(Date.now() + 30 * 86400000)),
    note: "",
  });
  const [saving, setSaving] = useState(false);
  const plan = getPlan(form.planKey);
  const { data: history } = useAsync(() => superService.planHistory(org.id), [org.id], []);

  const save = async () => {
    setSaving(true);
    try {
      // วันหมดอายุ = สิ้นวันที่เลือก
      await superService.setPlan(org, { ...form, expireAt: `${form.expireAt}T23:59:59` }, by);
      onSaved();
    } catch (err) {
      toast(err.message, "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      size="lg"
      title={tx(`แพ็กเกจของ ${org.name || org.id}`, `Plan for ${org.name || org.id}`)}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>{tx("ยกเลิก", "Cancel")}</Button>
          <Button variant="primary" disabled={saving} onClick={save}>{saving ? tx("กำลังบันทึก...", "Saving...") : tx("บันทึก", "Save")}</Button>
        </>
      }
    >
      <div className="card-crm u-mb-16" style={{ background: "#fff7ed", borderColor: "#fed7aa" }}>
        <div className="u-fs-13">
          {tx(
            "เปิด/แก้แพ็กเกจโดยไม่ผ่านการจ่าย QR (เช่น Ultimate หลังเซ็นสัญญา) — มีผลกับร้านทันที และบันทึกประวัติพร้อมเหตุผลไว้ (ไม่นับเป็นยอดขาย)",
            "Grants or changes the plan without a QR payment (e.g. Ultimate after signing) — takes effect immediately and is logged with a reason (not counted as sales)"
          )}
        </div>
      </div>
      <div className="grid-2" style={{ gap: 12 }}>
        <Select label={tx("แพ็กเกจ", "Plan")} value={form.planKey} options={PLANS.map((item) => ({ value: item.key, label: pick(item) }))} onChange={(event) => {
            const next = getPlan(event.target.value);
            setForm({ ...form, planKey: next.key, seats: next.perUser ? clampSeats(next.key, form.seats || next.minSeats) : "" });
          }} />
        {plan?.perUser ? (
          <Input
            label={tx(`จำนวน User (${plan.minSeats} - ${plan.maxSeats})`, `Users (${plan.minSeats} - ${plan.maxSeats})`)}
            type="number"
            value={form.seats}
            onChange={(event) => setForm({ ...form, seats: event.target.value })}
            onBlur={() => setForm({ ...form, seats: clampSeats(plan.key, form.seats) })}
          />
        ) : <div />}
        <Select label={tx("รอบบิล", "Billing cycle")} value={form.cycle} options={PLAN_CYCLES.map((item) => ({ value: item.key, label: pick(item) }))} onChange={(event) => setForm({ ...form, cycle: event.target.value })} />
        <Input label={tx("ใช้ได้ถึง", "Valid until")} type="date" value={form.expireAt} onChange={(event) => setForm({ ...form, expireAt: event.target.value })} />
      </div>
      <Textarea label={tx("เหตุผล / เลขที่สัญญา (บังคับ)", "Reason / contract no. (required)")} rows={2} value={form.note} onChange={(event) => setForm({ ...form, note: event.target.value })} />

      <div className="u-strong u-mt-16 u-mb-8">{tx("ประวัติแพ็กเกจ", "Plan history")}</div>
      <DataTable
        rows={history || []}
        columns={[
          { key: "createdAt", label: tx("วันที่", "Date"), render: (row) => formatDate(row.createdAt, true) },
          { key: "planName", label: tx("แพ็กเกจ", "Plan"), render: (row) => `${row.planName || row.plan}${row.seats ? ` · ${row.seats} User` : ""}` },
          { key: "expireAt", label: tx("ใช้ได้ถึง", "Valid until"), render: (row) => formatDate(row.expireAt) },
          { key: "amount", label: tx("ยอด", "Amount"), align: "right", render: (row) => baht(row.amount, 0) },
          { key: "status", label: tx("ที่มา", "Source"), render: (row) => (row.status === "manual" ? <Badge tone="amber">{tx(`ทีมงาน: ${row.note || ""}`, `Team: ${row.note || ""}`)}</Badge> : <Badge tone="green">{tx("ชำระ QR", "QR paid")}</Badge>) },
        ]}
        emptyText={tx("ยังไม่มีประวัติ", "No history")}
      />
    </Modal>
  );
}
